/**
 * The Settings "Support" section (PLAN-NEXT H2, D-N2).
 *
 * Support is in-app and it is a **thread**: one section, one import and one mount
 * in `routes/settings.tsx`, and everything the feature needs is in this file. It
 * carries its own section shell rather than reaching for the page's private
 * `Section`/`Row` helpers — the class lists are copied deliberately so the two
 * read as one page, and the section can be moved or removed without touching
 * anything else.
 *
 * ## Two views, never both
 *
 * The list (my threads, plus the form that opens one) and the thread (the
 * conversation, plus a reply box) swap places in the same card. A settings page is
 * not a mail client: nesting a scrolling transcript inside a row of a list of rows
 * would be the beginning of one, and "back to my tickets" is one click.
 *
 * It is a **master → detail** pair and it is built like one, because anything
 * else makes the swap feel like a trick. The whole list row is the way in — not a
 * button parked at the end of it — and the way back **leads** the detail view:
 * top-left, first in reading order, a chevron and "All conversations", which is
 * where the eye looks for it in every mail client, file browser and settings app
 * anybody has ever used. The right-hand side of a header is where *actions* live,
 * so what sits there is the status, and going back is not an action on the thread.
 *
 * ## Signed out, it renders nothing
 *
 * The whole settings page is reachable signed out — the workspace lives in
 * IndexedDB until you sign in, which is a supported way to use the product — so
 * this section returns `null` for a guest rather than offering a form whose POST
 * would 401. The Account section above it already carries the "sign in" link, and
 * a second one here would be the third thing on the page telling somebody the
 * same fact.
 *
 * ## What the copy has to be honest about
 *
 * Two things, both stated in the form's hint rather than buried:
 *
 * - **The conversation lives here.** Email is a doorbell — "you have a reply" —
 *   and replying to that mail reaches nobody (D-N2). Somebody who does not know
 *   that will reply to it.
 * - **Threads outlive a deleted account, anonymised.** `worker/account.ts` keeps
 *   the conversation and drops the identity, for reasons that are good but are
 *   *ours*; the person typing into the box is entitled to know before they type.
 *   Screenshots are the exception and are deleted outright, which is the one
 *   direction of that trade nobody needs warning about.
 *
 * ## Screenshots
 *
 * Both boxes — the new ticket and the reply — carry an attach control, because a
 * bug report is usually about something that happened on a screen and the most
 * useful thing somebody can send is the picture of it. The caps are checked here
 * before the send, in the *server's own sentences* (`checkSupportImages`), so a
 * refusal costs a file picker rather than a round trip and the paragraph they had
 * typed. The server checks all of it again from the bytes; nothing on this page
 * is a control.
 */

import { ChevronLeftIcon, ChevronRightIcon, PaperclipIcon, XIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useSession } from '@/auth/session-context';
import { BOARD_PAGE_PATH } from '@/board/protocol';
import { Skeleton } from '@/components/skeleton';
import { useToast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatRelative } from '@/lib/format';
import { USER_IMAGE_CLASS, userContent } from '@/lib/posthog';
import { cn } from '@/lib/utils';
import {
  checkSupportImages,
  closeSupportTicket,
  createSupportTicket,
  fetchSupportThread,
  replySupportTicket,
  useSupportTickets,
} from '@/support/client';
import type {
  SupportCategory,
  SupportMessage,
  SupportThread,
  SupportTicketSummary,
} from '@/support/protocol';
import {
  attachmentsOf,
  awaitingUser,
  SUPPORT_BODY_MAX_LENGTH,
  SUPPORT_CATEGORIES,
  SUPPORT_CATEGORY_LABELS,
  SUPPORT_IMAGE_CONTENT_TYPES,
  SUPPORT_SECTION_ID,
  SUPPORT_SUBJECT_MAX_LENGTH,
  supportAttachmentPath,
} from '@/support/protocol';

/** How each status reads to the person who opened the ticket. */
const STATUS_LABELS: Readonly<Record<string, string>> = {
  open: 'Waiting on us',
  answered: 'Reply from us',
  closed: 'Closed',
};

/**
 * The section.
 *
 * Exported on its own so `settings.tsx` gains one import and one line, and so a
 * test can render the section without the page (and therefore without the
 * workspace store, the sync provider and the theme provider it does not use).
 */
export function SupportSection() {
  const session = useSession();
  const signedIn = session.user !== null;
  const tickets = useSupportTickets(signedIn);
  const [open, setOpen] = useState<SupportThread | null>(null);

  // A guest has nowhere to put a ticket. See this file's header.
  if (!signedIn) return null;

  return (
    <section id={SUPPORT_SECTION_ID} className="flex flex-col gap-1">
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
        Support
      </h2>
      <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
        {open === null ? (
          <>
            <NewTicketRow
              onOpened={(thread) => {
                setOpen(thread);
                tickets.refresh();
              }}
            />
            <TicketList
              state={tickets}
              onSelect={(thread) => {
                setOpen(thread);
              }}
            />
          </>
        ) : (
          <ThreadView
            thread={open}
            onChanged={(next) => {
              setOpen(next);
              tickets.refresh();
            }}
            onBack={() => {
              setOpen(null);
              tickets.refresh();
            }}
          />
        )}
      </div>
    </section>
  );
}

/**
 * The form that opens a thread.
 *
 * Category, subject, message, send — in that order, because the category changes
 * how the message should be written ("what did you expect to happen" belongs in a
 * bug and nowhere else) and choosing it first is what makes the rest obvious.
 *
 * On success it jumps straight into the new thread rather than clearing the form
 * and showing a list row. What somebody wants after sending is proof that it
 * arrived, and the transcript is that proof.
 *
 * ## Picking "Feature request" offers the board, and does not insist
 *
 * The category stays selectable and the form goes on working, because a private
 * feature conversation is a legitimate thing to want — somebody describing an
 * idea tied to a recipe they have not published, or who simply does not want to
 * post in public, is asking for the right thing and should not be argued with by
 * a disabled control. What the sentence buys is the case the board is genuinely
 * better for: a request filed as a ticket cannot be voted for, so every duplicate
 * costs a conversation and no demand signal accumulates. One line, under the
 * select, with a link.
 */
/**
 * The subject a new ticket starts with.
 *
 * Every page on docs.beerwright.com ends with "Something wrong on this page?
 * Tell us", linking here with the docs path in `?about=` (PLAN-DOCS A4d). That
 * is the honest version of Brewfather's yes / not-sure / no widget: a vote
 * nobody can act on becomes a ticket that says which page it came from.
 *
 * Read straight off `window.location.search` rather than through the router's
 * typed search, following `lib/paddle.ts`'s `_ptxn`: the app declares no search
 * schema anywhere, and adding `validateSearch` to `/support/$tab` to carry one
 * optional string would put a route contract in the way of a prefill. It seeds
 * the initial state only, so typing over it works and a re-render never puts it
 * back.
 */
function initialSubject(): string {
  try {
    const about = new URLSearchParams(window.location.search).get('about');
    // A path, and only a path: this ends up in a ticket somebody reads, and an
    // unbounded query parameter is a stranger's sentence in our queue.
    if (about === null || !about.startsWith('/') || about.length > 120) return '';
    return `Docs: ${about}`;
  } catch {
    return '';
  }
}

function NewTicketRow({ onOpened }: { readonly onOpened: (thread: SupportThread) => void }) {
  const { toast } = useToast();
  const [category, setCategory] = useState<SupportCategory>('bug');
  const [subject, setSubject] = useState(initialSubject);
  const [message, setMessage] = useState('');
  const [images, setImages] = useState<readonly File[]>([]);
  const [busy, setBusy] = useState(false);

  const ready = subject.trim() !== '' && message.trim() !== '' && !busy;

  async function send(): Promise<void> {
    if (!ready) return;
    setBusy(true);
    const result = await createSupportTicket(
      { category, subject: subject.trim(), message: message.trim() },
      images,
    );
    setBusy(false);
    if (!result.ok) {
      toast({ variant: 'error', title: 'Nothing sent', description: result.message });
      return;
    }
    setSubject('');
    setMessage('');
    // Only after the server took them: a failed send has to leave the whole
    // message — screenshots included — exactly where it was.
    setImages([]);
    toast({
      title: 'Ticket opened',
      description: 'We will reply in the app, and email you when we do.',
    });
    onOpened(result.value);
  }

  return (
    <div className="flex flex-col gap-3 px-4 py-3">
      <div>
        <p className="text-sm font-medium">Ask us something</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          The conversation stays in the app. We'll email you when there's a reply, but replying to
          that email won't reach us. Support conversations are kept after an account is deleted,
          with the name and email address removed from them.
        </p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Select value={category} onValueChange={(value) => setCategory(value as SupportCategory)}>
          <SelectTrigger size="sm" aria-label="What is this about" className="w-full sm:w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SUPPORT_CATEGORIES.map((value) => (
              <SelectItem key={value} value={value}>
                {SUPPORT_CATEGORY_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={subject}
          aria-label="Subject"
          placeholder="One line: what is going on?"
          maxLength={SUPPORT_SUBJECT_MAX_LENGTH}
          data-testid="support-subject"
          className="h-8 flex-1"
          onChange={(event) => setSubject(event.target.value)}
        />
      </div>
      {category === 'feature' && (
        <p data-testid="support-board-steer" className="text-muted-foreground text-xs">
          Feature requests do better on the board, where other brewers can vote on them —{' '}
          <a
            href={BOARD_PAGE_PATH}
            className="font-medium text-foreground underline underline-offset-2"
          >
            post it there instead
          </a>
          .
        </p>
      )}
      <Textarea
        value={message}
        aria-label="Message"
        placeholder="What happened, what you expected, and anything you have already tried."
        maxLength={SUPPORT_BODY_MAX_LENGTH}
        data-testid="support-message"
        onChange={(event) => setMessage(event.target.value)}
      />
      <AttachmentPicker
        images={images}
        onChange={setImages}
        onRefused={(description) => toast({ variant: 'error', title: 'Not attached', description })}
        idPrefix="support-new"
      />
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          disabled={!ready}
          data-testid="support-send"
          onClick={() => void send()}
        >
          {busy ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </div>
  );
}

/**
 * My threads, newest activity first. Opening one is a fetch, not a cached row.
 *
 * The three states the loading plan names, in the order they are reached
 * (D-LD1). With nothing kept, the rows' own shape stands where the rows will
 * be, because a sentence there changes the height of the panel when it goes.
 * With something kept — every refresh after a send, a close or a Back — the
 * conversations stay on screen, dimmed and marked busy, and the new answer
 * replaces them in place. A read we could not make keeps them too and says so
 * in one line with the way back beside it, because "we could not ask" is not
 * "there are none" (D-LD2, D-LD9).
 *
 * The hook reports its failure as `error: string | null` rather than a flag, so
 * a kept list reads it as "there is something stale here" and an empty one
 * still shows the server's own sentence.
 */
function TicketList({
  state,
  onSelect,
}: {
  readonly state: ReturnType<typeof useSupportTickets>;
  readonly onSelect: (thread: SupportThread) => void;
}) {
  const { toast } = useToast();
  const kept = state.tickets.length > 0;
  const refreshing = state.loading && kept;

  if (state.loading && !kept) {
    // In the list's own layout, so the panel is the right height before the
    // conversations land and does not resize when they do (D-LD4).
    return (
      <Skeleton
        rows={FIRST_LOAD_ROWS}
        label="Reading your conversations…"
        className="flex flex-col gap-2 px-4 py-3"
        testId="support-skeleton"
      />
    );
  }
  if (state.error !== null && !kept) {
    return (
      <div className="flex flex-col gap-2 px-4 py-6">
        <p className="text-sm text-destructive">{state.error}</p>
        <button
          type="button"
          onClick={state.refresh}
          className="w-fit text-sm text-primary underline-offset-4 hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!kept) {
    return (
      <p className="px-4 py-6 text-sm text-muted-foreground">You have not asked us anything yet.</p>
    );
  }

  return (
    <>
      {state.error !== null && (
        <p
          role="status"
          data-testid="support-stale"
          className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-sm text-muted-foreground"
        >
          We couldn't reach your conversations, so you're seeing what we loaded last time.{' '}
          <Button type="button" variant="outline" size="sm" onClick={state.refresh}>
            Try again
          </Button>
        </p>
      )}
      {refreshing && (
        <p className="sr-only" role="status">
          Updating your conversations…
        </p>
      )}
      {/* The rows keep their own dividers inside this wrapper, because the
          panel around them draws one between each of its children and the
          rows are one child of it now. */}
      <div
        aria-busy={refreshing}
        className={cn('divide-y divide-border', refreshing && 'opacity-60 transition-opacity')}
      >
        {state.tickets.map((ticket) => (
          <TicketRow key={ticket.id} ticket={ticket} onSelect={onSelect} onError={toast} />
        ))}
      </div>
    </>
  );
}

/**
 * How many placeholder rows stand in for the list.
 *
 * Three rather than five: five is the cap on open conversations, and most
 * people asking us something have one or two going. The placeholder is a guess
 * at the usual page, not at the busiest one (D-LD4).
 */
const FIRST_LOAD_ROWS = 3;

/**
 * One thread in the list, and the whole row is the way into it.
 *
 * A row with an **Open** button at the end of it makes the row look like a
 * record with an action on it; a list of conversations is a list of things you
 * go *into*, and the target for that is the row. So the row is the button — one
 * `<button>` wrapping the subject, the hint and the status, with a chevron
 * saying where it leads — which is also a target somebody can hit on a phone
 * rather than a 60-pixel word at the far edge of it.
 *
 * The children are `<span>`s rather than `<p>`s because a paragraph inside a
 * button is invalid HTML, and the testids are the ones the row already carried:
 * `support-open-<id>` still means "the way into this thread", it is simply the
 * whole row now.
 */
function TicketRow({
  ticket,
  onSelect,
  onError,
}: {
  readonly ticket: SupportTicketSummary;
  readonly onSelect: (thread: SupportThread) => void;
  readonly onError: ReturnType<typeof useToast>['toast'];
}) {
  const [busy, setBusy] = useState(false);
  const last = ticket.lastMessage;

  return (
    <button
      type="button"
      disabled={busy}
      data-testid={`support-open-${ticket.id}`}
      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 disabled:opacity-60"
      onClick={async () => {
        setBusy(true);
        const result = await fetchSupportThread(ticket.id);
        setBusy(false);
        if (!result.ok) {
          onError({ variant: 'error', title: 'Could not open it', description: result.message });
          return;
        }
        onSelect(result.value);
      }}
    >
      <span className="min-w-0 flex-1">
        <span {...userContent} className="block truncate text-sm font-medium">
          {ticket.subject}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {[
            categoryLabel(ticket.category),
            last === null
              ? null
              : `${last.author === 'admin' ? 'We replied' : 'You wrote'} ${formatRelative(
                  last.createdAt * 1000,
                )}`,
            `${ticket.messageCount} ${ticket.messageCount === 1 ? 'message' : 'messages'}`,
          ]
            .filter((part) => part !== null)
            .join(' · ')}
        </span>
      </span>
      <Badge
        variant={awaitingUser(ticket) ? 'default' : 'muted'}
        className="text-xs"
        data-testid={`support-status-${ticket.id}`}
      >
        {STATUS_LABELS[ticket.status] ?? ticket.status}
      </Badge>
      <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

/**
 * One conversation, oldest message first, with the reply box under it.
 *
 * Oldest first because a support thread is read forwards — the question is the
 * context for every answer after it — which is the opposite of the console's
 * queue, and the opposite for the same reason: an operator scans for what is new,
 * a participant reads what was said.
 *
 * Every mutation replaces the thread with the server's copy (that is what these
 * routes return), so the transcript on screen is never a local splice.
 */
function ThreadView({
  thread,
  onChanged,
  onBack,
}: {
  readonly thread: SupportThread;
  readonly onChanged: (next: SupportThread) => void;
  readonly onBack: () => void;
}) {
  const { toast } = useToast();
  const [reply, setReply] = useState('');
  const [images, setImages] = useState<readonly File[]>([]);
  const [busy, setBusy] = useState(false);

  async function send(): Promise<void> {
    if (reply.trim() === '' || busy) return;
    setBusy(true);
    const result = await replySupportTicket(thread.id, reply.trim(), images);
    setBusy(false);
    if (!result.ok) {
      toast({ variant: 'error', title: 'Nothing sent', description: result.message });
      return;
    }
    setReply('');
    setImages([]);
    onChanged(result.value);
  }

  return (
    <>
      <div className="flex flex-col gap-2 px-4 py-3">
        <Button
          size="sm"
          variant="ghost"
          className="-ml-2 h-7 w-fit gap-1 px-2 text-xs text-muted-foreground"
          data-testid="support-back"
          onClick={onBack}
        >
          <ChevronLeftIcon className="size-4" />
          All conversations
        </Button>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <p {...userContent} className="text-sm font-medium">
              {thread.subject}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {categoryLabel(thread.category)} · opened {formatRelative(thread.createdAt * 1000)}
            </p>
          </div>
          <Badge variant="muted" className="shrink-0 text-xs">
            {STATUS_LABELS[thread.status] ?? thread.status}
          </Badge>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-4 py-3">
        {thread.messages.map((message) => (
          <div key={message.id} className="flex flex-col gap-1">
            <p className="text-xs font-medium text-muted-foreground">
              {message.author === 'admin' ? 'Beerwright' : 'You'} ·{' '}
              {formatRelative(message.createdAt * 1000)}
            </p>
            {/* Both authors': the brewer's question and our reply quote each
                other, and the reply repeats whatever they told us. */}
            <p {...userContent} className="text-sm whitespace-pre-wrap">
              {message.body}
            </p>
            <MessageAttachments ticketId={thread.id} message={message} />
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 px-4 py-3">
        <Textarea
          value={reply}
          aria-label="Your reply"
          placeholder={
            thread.status === 'closed'
              ? 'Replying reopens this conversation.'
              : 'Add to this conversation.'
          }
          maxLength={SUPPORT_BODY_MAX_LENGTH}
          data-testid="support-reply"
          onChange={(event) => setReply(event.target.value)}
        />
        <AttachmentPicker
          images={images}
          onChange={setImages}
          onRefused={(description) =>
            toast({ variant: 'error', title: 'Not attached', description })
          }
          idPrefix="support-reply"
        />
        <div className="flex flex-wrap justify-end gap-2">
          {thread.status !== 'closed' && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              data-testid="support-close"
              onClick={async () => {
                setBusy(true);
                const result = await closeSupportTicket(thread.id);
                setBusy(false);
                if (!result.ok) {
                  toast({ variant: 'error', title: 'Still open', description: result.message });
                  return;
                }
                onChanged(result.value);
              }}
            >
              Close it
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={busy || reply.trim() === ''}
            data-testid="support-reply-send"
            onClick={() => void send()}
          >
            {busy ? 'Sending…' : 'Reply'}
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * The attach control, and a chip for each file waiting to go.
 *
 * A hidden `<input type="file">` driven by a ghost button, because the native
 * control is a grey box with a filename beside it that no amount of CSS makes
 * belong on this page — and because "Attach a screenshot" says what the button
 * is for where "Choose files" does not.
 *
 * The files live in the *parent's* state rather than here, which is what lets a
 * send clear them and a failed send keep them. Selection is additive: picking one
 * file and then another gives two chips, because that is what somebody who
 * screenshotted two things does, and a picker that replaced the first would be a
 * silent loss.
 *
 * {@link checkSupportImages} runs on every addition and refuses the *whole*
 * addition rather than filtering it. Dropping the offending file quietly and
 * keeping the rest would leave somebody sending a message about a screenshot
 * that is not attached to it. The server checks all of it again regardless — this
 * is a courtesy, not a control.
 *
 * The one thing an addition does drop silently is a file that is already on the
 * list ({@link fileKey}). Picking the same screenshot twice is a slip rather than
 * an intent — nobody means to send one image two times — and it is also what
 * makes each chip's key a fact about the file rather than its position, so
 * removing the first of three does not renumber the other two under React.
 */
function AttachmentPicker({
  images,
  onChange,
  onRefused,
  idPrefix,
}: {
  readonly images: readonly File[];
  readonly onChange: (next: readonly File[]) => void;
  readonly onRefused: (message: string) => void;
  /** Distinguishes the new-ticket picker from the reply one, in tests and in ids. */
  readonly idPrefix: string;
}) {
  const input = useRef<HTMLInputElement | null>(null);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={input}
        type="file"
        accept={SUPPORT_IMAGE_CONTENT_TYPES.join(',')}
        multiple
        hidden
        data-testid={`${idPrefix}-file`}
        onChange={(event) => {
          const already = new Set(images.map(fileKey));
          const added = Array.from(event.target.files ?? []).filter(
            (file) => !already.has(fileKey(file)),
          );
          // Cleared either way, so picking the same file again after removing it
          // still fires a change event.
          event.target.value = '';
          if (added.length === 0) return;

          const chosen = [...images, ...added];
          const problem = checkSupportImages(chosen);
          if (problem !== null) {
            onRefused(problem);
            return;
          }
          onChange(chosen);
        }}
      />
      <Button
        size="sm"
        variant="ghost"
        data-testid={`${idPrefix}-attach`}
        onClick={() => input.current?.click()}
      >
        <PaperclipIcon className="size-4" />
        Attach a screenshot
      </Button>
      {images.map((file, index) => (
        <span
          key={fileKey(file)}
          className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground"
          data-testid={`${idPrefix}-chip-${index}`}
        >
          <span {...userContent} className="max-w-40 truncate">
            {file.name}
          </span>
          <span className="shrink-0">{fileSize(file.size)}</span>
          <button
            type="button"
            aria-label={`Remove ${file.name}`}
            data-testid={`${idPrefix}-remove-${index}`}
            className="text-muted-foreground hover:text-foreground"
            onClick={() => onChange(images.filter((_, at) => at !== index))}
          >
            <XIcon className="size-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

/**
 * The screenshots on one message, as thumbnails.
 *
 * Capped in height rather than cropped to a square: a screenshot is
 * predominantly wide, and a square thumbnail of one is a thumbnail of its
 * top-left corner. Each is wrapped in a link to the same URL so full size is one
 * click, and `loading="lazy"` keeps a long thread from fetching a dozen images
 * nobody scrolled to.
 *
 * The alt text is positional — "Attachment 2" — because nothing here knows what
 * the picture shows and inventing a description would be worse than admitting
 * that. The file name is deliberately not stored (`support/protocol.ts`).
 */
function MessageAttachments({
  ticketId,
  message,
}: {
  readonly ticketId: string;
  readonly message: SupportMessage;
}) {
  const attachments = attachmentsOf(message);
  if (attachments.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-2">
      {attachments.map((attachment, index) => {
        const href = supportAttachmentPath(ticketId, attachment.id);
        return (
          <a key={attachment.id} href={href} target="_blank" rel="noreferrer">
            {/* A screenshot of their studio, which is a picture of their
                recipes. Blocked outright, like an avatar. */}
            <img
              src={href}
              alt={`Attachment ${index + 1}`}
              loading="lazy"
              className={cn('max-h-40 rounded-md border border-border', USER_IMAGE_CLASS)}
            />
          </a>
        );
      })}
    </div>
  );
}

/**
 * What makes two picked files the same file.
 *
 * Name, size and modification time — the three facts a browser will tell you
 * about a `File` without reading it. It is not a hash and does not need to be:
 * the question is "did they pick this one twice", not "are these bytes
 * identical", and reading megabytes to answer the first would be absurd.
 */
function fileKey(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

/**
 * A file's size, for a chip.
 *
 * Local rather than the console's `formatBytes`, which lives in `admin/format.ts`
 * behind a header that says it is the ops console's vocabulary — this is one
 * label on a settings page, and importing the console's formatting into it would
 * be the first thread of a dependency nothing here wants.
 */
function fileSize(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1000 * 1000) return `${Math.round(bytes / 1000)} kB`;
  return `${(bytes / (1000 * 1000)).toFixed(1)} MB`;
}

/** A category as a human reads it; one this build has retired reads as itself. */
function categoryLabel(category: string): string {
  return SUPPORT_CATEGORY_LABELS[category as SupportCategory] ?? category;
}
